import { FileText, Calendar, Wallet } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";

import { featureNavGroups } from "@/content/nav";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import { NavDropdownMenu } from "./NavDropdownMenu";

const FEATURE_ICONS = {
  script: FileText,
  schedule: Calendar,
  budgeting: Wallet,
} as const;

const FEATURE_DESCRIPTION_KEYS = {
  script: "nav.featuresScriptDescription",
  schedule: "nav.featuresScheduleDescription",
  budgeting: "nav.featuresBudgetingDescription",
} as const;

export const NavFeaturesMenu = () => {
  const t = useTranslations();
  const pathname = usePathname();
  const isFeaturesActive =
    pathname === "/features" || pathname.startsWith("/features/");

  return (
    <NavDropdownMenu
      label={t("nav.whatItDoes")}
      isActive={isFeaturesActive}
      contentClassName="w-[260px] max-w-[260px]"
    >
      <div className="flex flex-col gap-0.5">
        {featureNavGroups.map((group, groupIndex) => (
          <div
            key={group.labelKey ?? `group-${groupIndex}`}
            className="flex flex-col gap-0.5"
          >
            {group.labelKey ? (
              <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {t(group.labelKey)}
              </p>
            ) : null}
            {group.items.map((item) => {
              const href = `/features/${item.slug}`;
              const isActive =
                pathname === href || pathname.startsWith(`${href}/`);
              const Icon = FEATURE_ICONS[item.slug];
              const descriptionKey = FEATURE_DESCRIPTION_KEYS[item.slug];

              return (
                <Link
                  key={item.slug}
                  href={href}
                  className={cn(
                    "flex gap-2.5 rounded-lg px-2.5 py-2 transition-colors hover:bg-muted hover:text-foreground",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  <Icon
                    className="mt-0.5 size-4 shrink-0 text-brand"
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span
                      className={cn(
                        "text-sm",
                        isActive
                          ? "font-medium"
                          : "font-medium text-foreground",
                      )}
                    >
                      {t(item.labelKey)}
                    </span>
                    <span className="text-[11px] leading-snug text-muted-foreground">
                      {t(descriptionKey)}
                    </span>
                  </span>
                </Link>
              );
            })}
            {groupIndex < featureNavGroups.length - 1 ? (
              <div className="my-1 h-px bg-border/60" aria-hidden />
            ) : null}
          </div>
        ))}

        <div className="mt-1 border-t border-zinc-200/45 dark:border-white/[0.05] pt-1">
          <Link
            href="/features"
            className={cn(
              "block rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-muted hover:text-foreground",
              pathname === "/features"
                ? "bg-muted font-medium text-foreground"
                : "font-medium text-foreground",
            )}
          >
            {t("nav.allFeatures")}
          </Link>
        </div>
      </div>
    </NavDropdownMenu>
  );
};
